import { createContext } from 'react'
import type { AvailResource } from '@/hooks/fhirSearchConfig.ts'
import {
  BEHUMAN_DOCUMENT_TYPE_SYSTEM,
  IMPORTED_TAG_CODE,
  IMPORTED_TAG_SYSTEM,
  PROGRESS_NOTE_DOC_TYPE_CODE,
} from '@/model/middleware-types'

/**
 * PD-2031 — the visit a Progress Note belongs to, or undefined for any other record. The note
 * editor stamps `context.encounter` on every note it saves; an uploaded or imported note has
 * none, even when it is coded like ours.
 */
export function progressNoteVisit(resource: AvailResource): string | undefined {
  if (resource.resourceType !== 'DocumentReference') return undefined
  if (!resource.type?.coding?.some((coding) => coding.code === PROGRESS_NOTE_DOC_TYPE_CODE)) {
    return undefined
  }
  const reference = resource.context?.encounter?.[0]?.reference
  return reference?.startsWith('Encounter/') ? reference : undefined
}

/**
 * PD-2031 — why a Progress Note cannot be removed from the chart, or undefined when it can.
 * Only a draft BeHuman generated qualifies: a locked note is the signed record of the visit,
 * and an imported, uploaded or otherwise unproven note is somebody else's document. The
 * server applies the same rule (`progressNoteRemovalBlock` in chart-record-permissions.ts).
 */
export function progressNoteRemovalBlock(resource: AvailResource): string | undefined {
  if (resource.resourceType !== 'DocumentReference') return undefined
  const isImported = (resource.meta?.tag ?? []).some(
    (tag) => tag.system === IMPORTED_TAG_SYSTEM && tag.code === IMPORTED_TAG_CODE,
  )
  const isBeHumanNote =
    !isImported &&
    !!progressNoteVisit(resource) &&
    (resource.type?.coding ?? []).some(
      (coding) =>
        coding.system === BEHUMAN_DOCUMENT_TYPE_SYSTEM &&
        coding.code === PROGRESS_NOTE_DOC_TYPE_CODE,
    )
  if (isBeHumanNote && (resource.docStatus === 'final' || resource.docStatus === 'amended')) {
    return 'Locked progress notes cannot be removed.'
  }
  return isBeHumanNote && resource.docStatus === 'preliminary'
    ? undefined
    : 'Only BeHuman-generated draft progress notes can be removed.'
}

/**
 * PD-2031 — tells the chart rail which note is open in the editor. Removing it from under the
 * editor would not stop the editor's closing save, and that save — which finds the visit's note
 * by encounter, never by id — would file the unsaved edits as a new draft.
 */
export const NoteEditorContext = createContext<(noteId: string | undefined) => void>(() => {})

export const NOTE_OPEN_IN_EDITOR = 'Close the editor before removing this progress note.'
